import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import { clearStoredTokens, createGuestUser, getValidAccessToken, registerUser, storeTokens } from '@/services/authService';
import ProgressIndicator from '@/components/ui/ProgressIndicator';
import BackArrow from '@/components/ui/BackArrow';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { cn } from '@/utils/cn';
import { pageContainer, pageItem } from '@/utils/animations';
import { analyticsService } from '@/services/analyticsService';
import { clearHistoryStack } from '@/utils/navigationHistory';
import { clearOnboardingProgress } from '@/utils/onboardingProgress';
import { clearDeferredOnboardingReturn } from '@/utils/onboardingDeferredReturn';

interface OnboardingNameProps {
  className?: string;
}

const OnboardingName = ({ className }: OnboardingNameProps) => {
  const navigate = useNavigate();
  const { updateUser, refreshAuthState } = useAuth();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // جایگزینی صفحه فعلی در history تا کاربر نتواند به صفحات قبلی برگردد
  useEffect(() => {
    window.history.replaceState(null, '', '/onboarding/name');
  }, []);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);

    if (value.length > 20) {
      setError('نام نمی‌تواند بیشتر از ۲۰ کاراکتر باشد');
      return;
    }
    
    // Clear error when user starts typing
    if (error && value.trim()) {
      setError('');
    }
  };

  const handleContinue = async () => {
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      setError('لطفاً نام خود را وارد کنید');
      return;
    }
    
    if (trimmedName.length < 3) {
      setError('نام باید حداقل ۳ حرف باشد');
      return;
    }

    if (trimmedName.length > 20) {
      setError('نام نمی‌تواند بیشتر از ۲۰ کاراکتر باشد');
      return;
    }

    const phoneNumber = sessionStorage.getItem('onboarding_phone_number') || '';
    if (!phoneNumber) {
      setError('شماره همراه پیدا نشد. لطفاً به مرحله قبل برگردید.');
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    
    // Save name to context and sessionStorage
    try {
      updateUser({ name: trimmedName });
      sessionStorage.setItem('onboarding_name', trimmedName);
      void analyticsService.logEvent('onboarding_name_submitted');

      // Registration must always run with a valid guest session. If the old
      // guest access/refresh session is gone, create a replacement guest and
      // continue with the onboarding data already collected in sessionStorage.
      const existingGuestAccessToken = await getValidAccessToken();
      if (!existingGuestAccessToken) {
        await clearStoredTokens();
        sessionStorage.removeItem('booka_guest_session');
        const guest = await createGuestUser();
        if (!guest.access_token || !guest.refresh_token) {
          throw new Error('نشست مهمان جدید از سرور دریافت نشد');
        }

        await storeTokens(guest.access_token, guest.refresh_token, guest.expires_in || 300, {
          persistNative: false,
        });
        sessionStorage.setItem('booka_guest_session', 'active');
        updateUser({ id: 'guest', authMethod: 'phone', is_guest: true, is_registered: false });
      }

      const storedTopicId = sessionStorage.getItem('onboarding_topic_id');
      const topicId = storedTopicId ? Number.parseInt(storedTopicId, 10) : NaN;
      const topicIds = Number.isInteger(topicId) && topicId > 0 ? [topicId] : undefined;
      const referralCode = sessionStorage.getItem('onboarding_referral_code') || undefined;

      const response = await registerUser({
        phone_number: phoneNumber,
        name: trimmedName,
        grant_default_trial: false,
        ...(topicIds ? { topic_ids: topicIds } : {}),
        ...(referralCode ? { referral_code: referralCode } : {}),
      });

      if (!response.access_token || !response.refresh_token) {
        throw new Error('توکن ثبت‌نام از سرور دریافت نشد');
      }

      await storeTokens(response.access_token, response.refresh_token, response.expires_in || 300);
      await refreshAuthState();
      await analyticsService.setUserIdFromAccessToken(response.access_token);
      void analyticsService.logEvent('onboarding_completed');

      clearOnboardingProgress();
      [
        'onboarding_topic_id',
        'onboarding_topic_name',
        'onboarding_phone_number',
        'onboarding_phone_verified',
        'onboarding_name',
        'onboarding_referral_code',
        'skip_books_selection',
        'xp_claim_state',
        'achievement_state',
        'onboarding_book_metadata',
        'booka_onboarding_started_at',
      ].forEach((key) => sessionStorage.removeItem(key));
      sessionStorage.removeItem('booka_guest_session');
      clearDeferredOnboardingReturn();
      localStorage.removeItem('onboarding_topic');

      clearHistoryStack('/dashboard');
      navigate('/dashboard', { replace: true });
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'خطا در ثبت‌نام');
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/onboarding/phone');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleContinue();
    }
  };

  return (
    <motion.div
      className={cn(
        'min-h-screen bg-white flex flex-col',
        'px-7 py-12',
        'relative overflow-hidden',
        className
      )}
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      dir="rtl"
    >
      {/* Back arrow in top-right corner for RTL */}
      <motion.div
        className="absolute top-6 right-6 z-10"
        variants={pageItem}
      >
        <BackArrow onClick={handleBack} variant="simple" />
      </motion.div>

      {/* Progress indicator */}
      <motion.div
        className="mb-16"
        variants={pageItem}
      >
        <ProgressIndicator currentStep={3} totalSteps={4} />
      </motion.div>

      {/* Name input section - centered */}
      <motion.div
        className="flex-1 flex flex-col justify-center mb-12 lg:mb-16 max-w-4xl lg:mx-auto w-full"
        variants={pageItem}
      >
        <div className="space-y-6">
          <div className="w-full max-w-form-input mx-auto space-y-3">
            <motion.input
              type="text"
              value={name}
              onChange={handleNameChange}
              onKeyPress={handleKeyPress}
              placeholder="اسمت رو وارد کن"
              className={cn(
                'w-full px-5 py-2.5 rounded-xl border-2 transition-all duration-300',
                'font-persian text-base text-foreground placeholder:text-muted',
                'focus:outline-none focus:ring-2 focus:ring-offset-1',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'text-center bg-soft-white shadow-sm',
                !error && [
                  'border-gray-200',
                  'hover:border-gray-300 hover:shadow-md',
                  'focus:border-gray-400 focus:ring-gray-200'
                ],
                error && [
                  'border-red-300 bg-red-50',
                  'hover:border-red-400',
                  'focus:border-red-500 focus:ring-red-200'
                ]
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />

            <div className="space-y-1 px-1 text-right font-persian text-xs leading-6 text-gray-500">
              <p>۱- این اسم برای همه کاربر ها نمایش داده میشه و تو رو به این اسم میشناسن</p>
              <p>۲- اسمت میتونه تکراری باشه و باید حداقل ۳ حرف باشه</p>
            </div>
            
            {error && (
              <motion.p
                className="text-red-500 text-sm font-persian text-center"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {error}
              </motion.p>
            )}
          </div>

          {/* Continue Button - below input */}
          <motion.div
            className="w-full max-w-form-input mx-auto"
            variants={pageItem}
          >
            <Button
              variant="primary"
              size="md"
              className="w-full rounded-xl"
              onClick={handleContinue}
              disabled={name.trim().length < 3 || name.trim().length > 20 || isSubmitting}
            >
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'ثبت نام'}
            </Button>
          </motion.div>
        </div>
      </motion.div>

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 rounded-full opacity-20"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-100 rounded-full opacity-20"></div>
        <div className="absolute top-1/4 -left-20 w-32 h-32 bg-accent rounded-full opacity-30"></div>
        <div className="absolute bottom-1/4 -right-20 w-24 h-24 bg-primary-200 rounded-full opacity-25"></div>
      </div>
    </motion.div>
  );
};

export default OnboardingName;
